import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { API_KEY_SCOPE_OPTIONS, maskApiKey } from "@/lib/apiKeyScopes";

interface ApiKey {
  _id: string;
  name?: string;
  key: string;
  scopes: string[];
  createdAt?: string;
}

export function SettingsApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  useEffect(() => {
    async function fetchApiKeys() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/api-keys");

        if (response.ok) {
          const data = await response.json();
          setApiKeys(data.apiKeys || []);
        } else if (response.status !== 401) {
          console.error("Failed to fetch API keys");
        }
      } catch (error) {
        console.error("Failed to fetch API keys:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchApiKeys();
  }, []);

  const handleOpenCreateDialog = () => {
    setKeyName("");
    setSelectedScopes([]);
    setIsCreateDialogOpen(true);
  };

  const toggleScope = (scope: string) => {
    setSelectedScopes((current) =>
      current.includes(scope)
        ? current.filter((value) => value !== scope)
        : [...current, scope]
    );
  };

  const handleCreate = async () => {
    if (selectedScopes.length === 0) {
      toast.error("Select at least one scope");
      return;
    }

    setIsCreating(true);
    try {
      const response = await fetch("/api/api-keys", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          scopes: selectedScopes,
          ...(keyName.trim() ? { name: keyName.trim() } : {}),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create API key");
      }

      const data = await response.json();
      setApiKeys([
        {
          ...data.apiKey,
          key: maskApiKey(data.apiKey.key),
        },
        ...apiKeys,
      ]);
      setIsCreateDialogOpen(false);
      setKeyName("");
      setSelectedScopes([]);
      setCreatedKey(data.apiKey.key);
      toast.success("API key created");
    } catch (error) {
      console.error("Error creating API key:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create API key");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (apiKeyId: string) => {
    if (!confirm("Are you sure you want to delete this API key?")) {
      return;
    }

    setDeletingId(apiKeyId);
    try {
      const response = await fetch(`/api/api-keys/${apiKeyId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete API key");
      }

      setApiKeys(apiKeys.filter((apiKey) => apiKey._id !== apiKeyId));
      toast.success("API key deleted");
    } catch (error) {
      console.error("Error deleting API key:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete API key");
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyCreatedKey = async () => {
    if (!createdKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(createdKey);
      toast.success("API key copied to clipboard");
    } catch (error) {
      console.error("Failed to copy API key:", error);
      toast.error("Failed to copy API key");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatScopes = (scopes: string[]) => {
    return scopes
      .map((scope) => API_KEY_SCOPE_OPTIONS.find((option) => option.value === scope)?.label ?? scope)
      .join(", ");
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">API Keys</h3>
            <p className="text-sm text-muted-foreground">
              Create API keys to access Fanrank programmatically
            </p>
          </div>
          <Button onClick={handleOpenCreateDialog}>Create API Key</Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Key</TableHead>
              <TableHead>Scopes</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiKeys.length > 0 ? (
              apiKeys.map((apiKey) => (
                <TableRow key={apiKey._id}>
                  <TableCell>{apiKey.name || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{apiKey.key}</TableCell>
                  <TableCell>{formatScopes(apiKey.scopes)}</TableCell>
                  <TableCell>
                    {apiKey.createdAt ? formatDate(apiKey.createdAt) : "-"}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(apiKey._id)}
                      disabled={deletingId === apiKey._id}
                      aria-label="Delete API key"
                    >
                      {deletingId === apiKey._id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No API keys yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Choose the scopes this key should have access to.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="api-key-name">Name</Label>
              <Input
                id="api-key-name"
                value={keyName}
                onChange={(event) => setKeyName(event.target.value)}
                placeholder="e.g. Production server"
              />
            </div>
            <div className="grid gap-3">
              <Label>Scopes</Label>
              {API_KEY_SCOPE_OPTIONS.map((scope) => (
                <label
                  key={scope.value}
                  htmlFor={`scope-${scope.value}`}
                  className="flex items-center gap-3 text-sm"
                >
                  <Checkbox
                    id={`scope-${scope.value}`}
                    checked={selectedScopes.includes(scope.value)}
                    onCheckedChange={() => toggleScope(scope.value)}
                  />
                  <span>{scope.label}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(createdKey)} onOpenChange={(open) => !open && setCreatedKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API Key Created</DialogTitle>
            <DialogDescription>
              Copy this key now. You will not be able to see it again.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted p-3 font-mono text-sm break-all">
            {createdKey}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatedKey(null)}>
              Done
            </Button>
            <Button onClick={handleCopyCreatedKey}>
              <Copy className="mr-2 h-4 w-4" />
              Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
